/*
When the Run button should stand out.

Activation is the product's biggest problem: visitors open examples, look at a
solved map, and leave without running anything. Examples load already solved, so
the button reads "Re-run optimization" in the same quiet outline as every other
control, and a newcomer has no reason to think it is the main action.

So the button is emphasised until it has done its job, and only for people who
haven't found it yet:
  - In their own scenario: until their first-ever run in this browser.
  - On an example (example mode): until they have run that example. Someone who
    has run before still gets the steady emphasis and hint on an example they
    haven't tried, but never the pulse.
  - The pulse plays at most once, ever, in this browser. Someone who uses the
    site every day must not be pulsed at in every new tab.

The memory is per browser (localStorage), not persisted redux state: it is a
convenience, it must not change the shape of anything redux-persist stores. Each
flag only ever goes from false to true (and the example list only grows), so a
save merges with what is already stored: a second tab holding an older copy can
never un-remember a run. Every storage call is wrapped, because storage can be
unavailable (private windows, blocked site data) and the page must work without
it; without storage the emphasis simply shows again on the next visit.
*/

export const RUN_EMPHASIS_STORAGE_KEY = 'optimiciti.runEmphasis';

// There are a few dozen examples at most; the cap only guards against a runaway list.
const MAX_EXAMPLES_REMEMBERED = 100;

const empty = () => ({ everRun: false, examplesRun: [], pulseShown: false });

const normalize = (value) => ({
  everRun: value?.everRun === true,
  examplesRun: Array.isArray(value?.examplesRun)
    ? value.examplesRun.filter((s) => typeof s === 'string').slice(-MAX_EXAMPLES_REMEMBERED)
    : [],
  pulseShown: value?.pulseShown === true,
});

/** The remembered state, or an empty one when there is nothing (or no storage). */
export function loadRunEmphasis() {
  try {
    const raw = window.localStorage.getItem(RUN_EMPHASIS_STORAGE_KEY);
    return raw ? normalize(JSON.parse(raw)) : empty();
  } catch (e) {
    return empty();
  }
}

/** Remember it in this browser, merged with anything another tab stored. Silently does nothing without storage. */
export function saveRunEmphasis(value) {
  try {
    const next = normalize(value);
    const stored = loadRunEmphasis();
    const examplesRun = [...stored.examplesRun];
    next.examplesRun.forEach((slug) => { if (!examplesRun.includes(slug)) examplesRun.push(slug); });
    window.localStorage.setItem(RUN_EMPHASIS_STORAGE_KEY, JSON.stringify({
      everRun: stored.everRun || next.everRun,
      examplesRun: examplesRun.slice(-MAX_EXAMPLES_REMEMBERED),
      pulseShown: stored.pulseShown || next.pulseShown,
    }));
  } catch (e) {
    /* storage unavailable: the emphasis simply shows again on the next visit */
  }
}

/**
 * Whether this person hasn't yet run the thing in front of them.
 *
 * @param {object} args
 * @param {boolean} args.inExample - example mode with a known example slug
 * @param {string|null} args.exampleSlug - the loaded example's slug
 * @param {boolean} args.everRun - has run anything in this browser
 * @param {string[]} args.examplesRun - slugs of examples run in this browser
 * @returns {boolean}
 */
export function isFirstRun({ inExample, exampleSlug, everRun, examplesRun }) {
  if (inExample && exampleSlug) return !(examplesRun || []).includes(exampleSlug);
  return !everRun;
}

/**
 * Whether to emphasise the Run button now: a first run (above) that would
 * actually go. An empty or invalid scenario, costs still being calculated, or a
 * full queue get no emphasis: pointing at a button that can only refuse helps
 * nobody.
 */
export function shouldEmphasizeRun({ runnable, ...rest }) {
  return Boolean(runnable) && isFirstRun(rest);
}

/**
 * Whether the one-time pulse is due: only for someone who has never run, and only
 * once ever. Under prefers-reduced-motion it is due but not played (the caller
 * marks it shown without animating).
 */
export function runPulseDue({ emphasized, everRun, pulseShown }) {
  return Boolean(emphasized) && !everRun && !pulseShown;
}
